<script setup lang="ts">
/**
 * 应用级总框架：左 168px sidebar + 右内容。
 *
 * Tauri 未登录时保留侧栏 + WelcomeGate + 登录弹窗；SaaS Web 未登录时先显示
 * 全屏登录门，不暴露工作台菜单。试用与正式账号都由运营线下审核开通。
 * 桌面授权:仅单机版 Tauri 先过 LicenseGate(填店铺名);云版跳过,直接欢迎/登录。
 */
import { computed, onMounted } from 'vue'
import Sidebar from './Sidebar.vue'
import ToastContainer from './ToastContainer.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import PromptDialog from './PromptDialog.vue'
import LoginModal from './LoginModal.vue'
import WelcomeGate from './WelcomeGate.vue'
import LicenseGate from './LicenseGate.vue'
import SupportDrawer from './SupportDrawer.vue'
import DesktopPet from './DesktopPet.vue'
import { useAuthStore } from '@/stores/auth'
import { useLicenseStore, GRACE_DAYS } from '@/stores/license'
import { isTauri } from '@/composables/useDesktopBridge'

const auth = useAuthStore()
const license = useLicenseStore()
const webFallback = !isTauri()

const needsLicenseGate = computed(
  () => license.enabled && license.ready && !license.shopName
)

const licenseBanner = computed(() => {
  if (!license.enabled || !license.shopName) return null
  if (license.status === 'expired') {
    return {
      tone: 'danger' as const,
      text: '试用/授权已到期，当前为只读模式。请联系我们续期。'
    }
  }
  if (license.status === 'grace') {
    const left = license.daysUntilExpiry + GRACE_DAYS
    return {
      tone: 'warning' as const,
      text: `授权已过期，宽限期内仍可正常使用（约 ${Math.max(0, left)} 天）。请尽快联系续期。`
    }
  }
  if (license.daysUntilExpiry <= 7 && license.daysUntilExpiry >= 0) {
    return {
      tone: 'warning' as const,
      text: `使用期限还剩 ${license.daysUntilExpiry} 天，请联系续期。`
    }
  }
  return null
})

onMounted(() => {
  void (async () => {
    await license.bootstrap()
    // 启动用持久化令牌恢复身份(只跑一次)。未登录则停在 WelcomeGate。
    void auth.bootstrap()
  })()
})
</script>

<template>
  <div v-if="webFallback && (!auth.ready || !license.ready)" class="web-boot"></div>
  <LoginModal v-else-if="webFallback && !auth.isAuthenticated && !auth.suspended" embedded />
  <div v-else class="layout">
    <Sidebar />
    <main class="content">
      <div v-if="auth.isImpersonating" class="imp-banner">
        <i class="ti ti-eye-search" aria-hidden="true"></i>
        <span>运营代登录 · 只读 —— 正在以「{{ auth.me?.tenant?.shop_name ?? '该档口' }}」身份查看，所有修改已禁用</span>
      </div>
      <div v-if="licenseBanner" class="lic-banner" :class="licenseBanner.tone">
        <i class="ti ti-alert-circle" aria-hidden="true"></i>
        <span>{{ licenseBanner.text }}</span>
      </div>
      <div v-if="!auth.ready || !license.ready" class="boot"></div>
      <LicenseGate v-else-if="needsLicenseGate" />
      <!-- 停服门:账号被暂停/注销 → 全屏提示,不进主界面。令牌保留(不清空回登录),
           让用户看清原因;可退出登录换账号。优先级高于欢迎门/内容。 -->
      <div v-else-if="auth.suspended" class="suspended">
        <i class="ti ti-lock-exclamation" aria-hidden="true"></i>
        <h2>账号已停用</h2>
        <p>{{ auth.suspended }}</p>
        <button class="logout-btn" type="button" @click="auth.logout()">退出登录</button>
      </div>
      <WelcomeGate v-else-if="!auth.isAuthenticated" />
      <slot v-else />
    </main>
    <ToastContainer />
    <ConfirmDialog />
    <PromptDialog />
    <LoginModal v-if="!webFallback" />
    <SupportDrawer />
    <DesktopPet />
  </div>
</template>

<style scoped>
.web-boot {
  position: fixed;
  inset: 0;
  background: var(--color-page-bg);
}

.layout {
  display: grid;
  grid-template-columns: 168px 1fr;
  height: 100vh;
  overflow: hidden;
}

.content {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  /* 始终预留竖滚动条槽:内容高/矮页面切换时滚动条不再一出一没,
     居中内容不会左右微跳(翻页"跳"感的隐蔽成因之一)。 */
  scrollbar-gutter: stable;
  background: var(--color-page-bg);
  min-height: 0;
  height: 100%;
}

/* 停服门全屏提示 */
.suspended {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  text-align: center;
  color: var(--color-text-secondary);
}
.suspended i {
  font-size: 48px;
  color: var(--color-text-warning, #ad6800);
}
.suspended h2 {
  font-size: var(--fs-page);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
  margin: 0;
}
.suspended p {
  font-size: var(--fs-item);
  max-width: 420px;
  line-height: 1.6;
  margin: 0;
}
.suspended .logout-btn {
  margin-top: 8px;
  padding: 8px 20px;
  font-size: var(--fs-control);
  font-weight: var(--fw-med);
  border-radius: var(--border-radius-md);
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-family: inherit;
}
.suspended .logout-btn:hover {
  background: var(--color-background-secondary);
}

/* 运营代登录只读条 —— 仅运营自己浏览器看到(租户无感知) */
.imp-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--color-background-warning, #fff7e6);
  color: var(--color-text-warning, #ad6800);
  font-size: var(--fs-control, 12px);
  font-weight: var(--fw-med, 500);
  border-bottom: 0.5px solid var(--color-border-warning, #ffe7ba);
  position: sticky;
  top: 0;
  z-index: 50;
}
.imp-banner i {
  font-size: 15px;
}

.lic-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: var(--fs-control, 12px);
  font-weight: var(--fw-med, 500);
  border-bottom: 0.5px solid;
  position: sticky;
  top: 0;
  z-index: 49;
}
.lic-banner.warning {
  background: var(--color-background-warning, #fff7e6);
  color: var(--color-text-warning, #ad6800);
  border-color: var(--color-border-warning, #ffe7ba);
}
.lic-banner.danger {
  background: var(--color-background-danger, #fff1f0);
  color: var(--color-text-danger, #cf1322);
  border-color: var(--color-border-danger, #ffccc7);
}
.lic-banner i {
  font-size: 15px;
}
</style>
